import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { inr } from "./lib";

export default function CounterSaleDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`/spares/counter-sales/${id}`)
      .then((r) => setS(r.data))
      .catch((e) => {
        toast.error(e.response?.data?.detail || "Could not load sale");
        nav("/spare-parts/counter-sales");
      })
      .finally(() => setLoading(false));
  }, [id, nav]);

  if (loading || !s) return <div className="py-10 text-center text-[13px] text-[#667085]">Loading…</div>;

  return (
    <div className="space-y-4" data-testid="counter-sale-detail-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Link to="/spare-parts/counter-sales" data-testid="counter-sale-detail-back"
                className="h-9 w-9 rounded-md border border-[#E4E7EC] grid place-items-center text-[#667085] hover:bg-[#F1FAEE]">
            <ArrowLeft size={15} />
          </Link>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight font-mono">{s.number}</h1>
              <span className="inline-flex px-2 py-0.5 rounded-full text-[10.5px] font-semibold uppercase tracking-wider bg-[#12B76A]/12 text-[#0F9256]">
                {s.payment_status || "Paid"}
              </span>
            </div>
            <div className="text-[12.5px] text-[#667085] mt-0.5">
              {s.customer_snapshot?.name || "Walk-in Customer"}
              {s.customer_snapshot?.phone && <> · <span className="font-mono">{s.customer_snapshot.phone}</span></>}
              {s.vehicle_reg && <> · <span className="font-mono">{s.vehicle_reg}</span></>}
              · {new Date(s.created_at).toLocaleString()}
            </div>
          </div>
        </div>
        <button onClick={() => window.print()} data-testid="counter-sale-print"
                className="h-10 px-4 rounded-md border border-[#E4E7EC] text-[12.5px] font-semibold text-[#1d3557] hover:bg-[#F1FAEE] inline-flex items-center gap-1.5">
          <Printer size={13} /> Print
        </button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <section className="lg:col-span-2 bg-white rounded-xl border border-[#E4E7EC] overflow-hidden">
          <div className="px-4 py-3 border-b border-[#F1FAEE]">
            <h3 className="text-[13px] font-bold text-[#1d3557]">Line items</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[10.5px] uppercase tracking-wider font-semibold text-[#667085] border-b border-[#F1FAEE]">
                  <th className="px-4 py-2.5">Part</th>
                  <th className="px-3 py-2.5 text-right">Qty</th>
                  <th className="px-3 py-2.5 text-right">Rate</th>
                  <th className="px-3 py-2.5 text-right">Disc%</th>
                  <th className="px-3 py-2.5 text-right">GST</th>
                  <th className="px-3 py-2.5 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {(s.lines || []).map((ln) => (
                  <tr key={ln.id} className="border-b border-[#F1FAEE] last:border-0">
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-[#1d3557]">{ln.name}</div>
                      {ln.part_number && <div className="text-[11px] font-mono text-[#667085]">{ln.part_number}</div>}
                    </td>
                    <td className="px-3 py-2.5 text-right font-mono">{ln.qty}</td>
                    <td className="px-3 py-2.5 text-right font-mono">{inr(ln.rate)}</td>
                    <td className="px-3 py-2.5 text-right text-[#667085]">{ln.discount_pct || 0}%</td>
                    <td className="px-3 py-2.5 text-right text-[#667085]">{ln.gst_rate || 0}%</td>
                    <td className="px-3 py-2.5 text-right font-mono font-bold">{inr(ln.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
        <aside>
          <section className="bg-white rounded-xl border border-[#E4E7EC] p-4 sticky top-4">
            <h3 className="text-[13px] font-bold text-[#1d3557] mb-3">Payment</h3>
            <div className="text-[12.5px] text-[#1d3557]">
              <div className="uppercase font-semibold text-[10.5px] tracking-wider text-[#667085]">Mode</div>
              <div className="text-[15px] font-bold">{s.payment_mode}</div>
            </div>
            {s.payment_reference && (
              <div className="text-[12.5px] text-[#1d3557] mt-2">
                <div className="uppercase font-semibold text-[10.5px] tracking-wider text-[#667085]">Reference</div>
                <div className="font-mono text-[13px]">{s.payment_reference}</div>
              </div>
            )}
            <div className="my-3 border-t border-[#F1FAEE]" />
            <div className="space-y-1 text-[12.5px]">
              <Row label="Subtotal" value={inr(s.totals?.subtotal)} />
              <Row label="Taxable" value={inr(s.totals?.taxable)} muted />
              {s.is_interstate
                ? <Row label="IGST" value={inr(s.totals?.igst)} muted />
                : (<>
                    <Row label="CGST" value={inr(s.totals?.cgst)} muted />
                    <Row label="SGST" value={inr(s.totals?.sgst)} muted />
                  </>)}
              {s.extra_discount ? <Row label="Extra discount" value={`− ${inr(s.extra_discount)}`} muted /> : null}
              <div className="my-2 border-t border-[#F1FAEE]" />
              <Row label="Grand total" value={inr(s.totals?.grand_total)} big />
            </div>
          </section>
        </aside>
      </div>
    </div>
  );
}

function Row({ label, value, muted, big }) {
  return (
    <div className={`flex items-center justify-between py-1 ${big ? "text-[15px] font-bold" : "text-[12.5px]"}`}>
      <span className={muted ? "text-[#667085]" : "text-[#1d3557]"}>{label}</span>
      <span className={`font-mono tabular-nums ${muted ? "text-[#667085]" : "text-[#1d3557]"}`}>{value}</span>
    </div>
  );
}
